import React, { useEffect } from 'react';
import styled from 'styled-components';
import { breakpoints } from '../../utils/styles';
import { SlideProps } from './Recap';
import StoryTrail from './StoryTrail';

const Slide = styled.div<{ prev?: boolean, next?: boolean, zIndex: number }>`
    & > div {
        align-items: center;
        bottom: 0;
        cursor: pointer;
        display: flex;
        height: 100%;
        justify-content: center;
        left: 50%;
        position: fixed;
        top: 0;
        transform: translateX(-50%);
        transition: all 0.5s ease-in-out;
        width: 100%;
        z-index: 100000;

        ${props => props.next && `z-index: ${props.zIndex};`}

        ${props => props.prev && 'left: -50%;'}
        ${props => props.next && 'left: 150%;'}

        @media (min-width: ${breakpoints.desktop}px) {
            aspect-ratio: 9 / 16;
            height: 90%;
            overflow: hidden;
            top: 50%;
            transform: translate(-50%, -50%);
            width: auto;
            
            ${props => props.prev && `
                left: 50%;
                transform: translate(-150%, -50%) scale(0.6);
            `}
            
            ${props => props.next && `
                left: 50%;
                transform: translate(50%, -50%) scale(0.6);
            `}
        }
    }
`;

interface Props {
    Content: React.ComponentType<SlideProps>;
    data: { [key: string]: any } | null;
    index: number;
    next: boolean;
    paused?: boolean;
    prev: boolean;
    total: number;
    zIndex: number;
    onClick: () => void;
    onMouseDown?: () => void;
    onMouseUp?: () => void;
    onNext: () => void;
    onPrev: () => void;
}

const StorySlide: React.FC<Props & SlideProps> = ({ prev, next, current, Content, data, paused, soundEnabled, total, index, zIndex, onClick, onNext, onPrev, onMouseDown, onMouseUp }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [scale, setScale] = React.useState(1);

    useEffect(() => {
        let startX: number | null = null;

        function handleTouchStart(e: TouchEvent) {
            if (e.touches.length === 1) {
                startX = e.touches[0].clientX;
            }
        }

        function handleTouchEnd(e: TouchEvent) {
            if (startX !== null && e.changedTouches.length === 1) {
                const endX = e.changedTouches[0].clientX;
                const diff = endX - startX;
                if (Math.abs(diff) > 50) {
                    if (diff > 0) {
                        onPrev();
                    } else {
                        onNext();
                    }
                }
            }
            startX = null;
        }

        const node = ref.current;
        if (node) {
            node.addEventListener('touchstart', handleTouchStart);
            node.addEventListener('touchend', handleTouchEnd);
        }
        return () => {
            if (node) {
                node.removeEventListener('touchstart', handleTouchStart);
                node.removeEventListener('touchend', handleTouchEnd);
            }
        };
    }, [onPrev, onNext, ref]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        handleResize();
    }, [ref]);
    
    function handleResize() {
        if (ref.current) {
            const width = ref.current.offsetWidth;
            const factor = width / 360;
            setScale(factor);
        }
    }

    function onClickHandler(e: React.MouseEvent<HTMLDivElement>) {
        if (e.target instanceof HTMLButtonElement) return;

        if (!current) {
            onClick();
            return;
        }
        
        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
        if (rect) {
            const clickX = e.clientX - rect.left;
            if (clickX < rect.width / 2) {
                onPrev();
            } else {
                onNext();
            }
        }
    }

    return <>
        <Slide next={next} prev={prev} zIndex={zIndex}>
            <div ref={ref} onClick={onClickHandler}>
                <Content scale={scale} current={current} soundEnabled={soundEnabled} data={data} />
                {current && <StoryTrail current={index} total={total} paused={paused} />}
            </div>
        </Slide>
    </>;
};

export default StorySlide;